import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const COOKIE_CONSENT_KEY = 'gold-flow-cookie-consent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait un choix
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    // Si aucun choix n'a été fait, afficher le popup
    if (!consent) {
      setShowConsent(true);
    } else {
      setShowConsent(false);
    }
  }, []);

  const handleAccept = () => {
    // Sauvegarder le choix dans localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowConsent(false);
  };

  const handleReject = () => {
    // Sauvegarder le choix dans localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    setShowConsent(false);
  };

  if (!showConsent) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 md:p-8 animate-fade-in">
        {/* En-tête */}
        <div className="flex items-start gap-4 mb-6">
          <div className="bg-gold-500 rounded-full p-3 flex-shrink-0">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl md:text-3xl font-bold text-navy-800 mb-2">
              {t('cookies.title')}
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              {t('cookies.subtitle')}
            </p>
          </div>
        </div>

        {/* Contenu */}
        <div className="mb-6 space-y-4">
          <p className="text-gray-700 leading-relaxed">
            {t('cookies.description')}
          </p>

          {/* Types de cookies */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-navy-700 mb-2">
              {t('cookies.types.title')}
            </h3>
            
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">{t('cookies.types.essential.title')}</p>
                  <p className="text-sm text-gray-600">{t('cookies.types.essential.description')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">{t('cookies.types.functional.title')}</p>
                  <p className="text-sm text-gray-600">{t('cookies.types.functional.description')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gold-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-gray-800">{t('cookies.types.analytics.title')}</p>
                  <p className="text-sm text-gray-600">{t('cookies.types.analytics.description')}</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 italic">
            {t('cookies.moreInfo')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={handleReject}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            {t('cookies.reject')}
          </button>
          <button
            onClick={handleAccept}
            className="px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
